"use server";

import { redirect } from "next/navigation";
import { db } from "@/lib/db";
import {
  clearLoginChallenge,
  createLoginChallenge,
  createSession,
  DUMMY_PASSWORD_HASH,
  resendLoginChallengeCode,
  verifyLoginChallenge,
  verifyPassword,
} from "@/lib/auth";
import { clientIp } from "@/lib/client-ip";
import { sendLoginCodeEmail } from "@/lib/emails";
import {
  clearRateLimit,
  formatRetryAfter,
  peekRateLimit,
  recordRateLimitHit,
  type RateLimitResult,
} from "@/lib/rate-limit";

const LOGIN_RATE_WINDOW_MS = 15 * 60 * 1000;
/** Per-IP failed attempts — only when TRUST_PROXY=1 provides a real IP. */
const LOGIN_IP_FAILURE_LIMIT = 15;
/** Per-email failed attempts — higher so multi-IP lockout is harder. */
const LOGIN_EMAIL_FAILURE_LIMIT = 25;
const RESEND_IP_LIMIT = 5;
const RESEND_RATE_WINDOW_MS = 10 * 60 * 1000;

export type LoginState =
  | null
  | { error: string; step?: "credentials" | "code" }
  | { step: "code"; message?: string };

export async function loginFormAction(
  prev: LoginState,
  formData: FormData
): Promise<LoginState> {
  const intent = String(formData.get("intent") ?? "login");
  if (intent === "verify") return verifyLoginCode(prev, formData);
  if (intent === "resend") return resendLoginCode(prev, formData);
  if (intent === "cancel") return cancelLoginCode();
  return login(prev, formData);
}

function rateLimitError(result: RateLimitResult): LoginState | null {
  if (result.ok) return null;
  return {
    error: `Too many sign-in attempts. Try again in ${formatRetryAfter(result.retryAfterSec)}.`,
    step: "credentials",
  };
}

async function login(
  _prev: LoginState,
  formData: FormData
): Promise<LoginState> {
  const email = String(formData.get("email") ?? "")
    .trim()
    .toLowerCase();
  const password = String(formData.get("password") ?? "");
  const ip = await clientIp();
  const emailKey = `login:email:${email}`;
  const ipKey = ip ? `login:ip:${ip}` : null;

  const blocked =
    (ipKey
      ? rateLimitError(
          peekRateLimit(ipKey, LOGIN_IP_FAILURE_LIMIT, LOGIN_RATE_WINDOW_MS)
        )
      : null) ??
    rateLimitError(
      peekRateLimit(emailKey, LOGIN_EMAIL_FAILURE_LIMIT, LOGIN_RATE_WINDOW_MS)
    );
  if (blocked) return blocked;

  const user = await db.user.findUnique({ where: { email } });
  const passwordOk = await verifyPassword(
    password,
    user?.passwordHash ?? DUMMY_PASSWORD_HASH
  );
  if (!user || !passwordOk) {
    if (ipKey) recordRateLimitHit(ipKey, LOGIN_RATE_WINDOW_MS);
    recordRateLimitHit(emailKey, LOGIN_RATE_WINDOW_MS);
    return { error: "Invalid email or password.", step: "credentials" };
  }

  clearRateLimit(emailKey);

  const code = await createLoginChallenge(user.id);
  try {
    await sendLoginCodeEmail(user.email, code);
  } catch {
    await clearLoginChallenge();
    return {
      error: "Could not send the verification email. Try again.",
      step: "credentials",
    };
  }
  // PRG: refresh must be a GET and must not resend the code.
  redirect("/admin/login");
}

async function verifyLoginCode(
  _prev: LoginState,
  formData: FormData
): Promise<LoginState> {
  const code = String(formData.get("code") ?? "").trim();
  if (!/^\d{6}$/.test(code)) {
    return { error: "Enter the 6-digit code from your email.", step: "code" };
  }

  const result = await verifyLoginChallenge(code);
  if (!result.ok) {
    return {
      error: result.error,
      step: result.reason === "wrong_code" ? "code" : "credentials",
    };
  }

  await createSession(result.userId);
  redirect("/admin");
}

async function resendLoginCode(
  _prev: LoginState,
  _formData: FormData
): Promise<LoginState> {
  const ip = await clientIp();
  const ipKey = ip ? `login-resend:ip:${ip}` : null;
  if (ipKey) {
    const resendBlocked = peekRateLimit(
      ipKey,
      RESEND_IP_LIMIT,
      RESEND_RATE_WINDOW_MS
    );
    if (!resendBlocked.ok) {
      return {
        error: `Too many resend attempts. Try again in ${formatRetryAfter(resendBlocked.retryAfterSec)}.`,
        step: "code",
      };
    }
  }

  const result = await resendLoginChallengeCode();
  if (!result.ok) {
    return { error: result.error, step: "credentials" };
  }

  try {
    await sendLoginCodeEmail(result.email, result.code);
  } catch {
    await clearLoginChallenge();
    return {
      error: "Could not send the verification email. Try again.",
      step: "credentials",
    };
  }
  if (ipKey) recordRateLimitHit(ipKey, RESEND_RATE_WINDOW_MS);
  redirect("/admin/login?resent=1");
}

async function cancelLoginCode(): Promise<LoginState> {
  await clearLoginChallenge();
  redirect("/admin/login");
}
